import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import './EditDietModal.css'
import Button from '../../Button/Button';
import DietTable from '../../DietTable/DietTable';
import AlternativesDietTable from '../../AlternativesDietTable/AlternativesDietTable';
import CircularProgress from '@mui/material/CircularProgress';
import { useAppContext } from '../../../context/AppContext';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import TextField from '@mui/material/TextField';
import MuiSelect from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import 'dayjs/locale/en-gb';
import { muiDatePickerSx, muiSelectSx, muiMenuItemSx } from '../../../utils/functions';
import { useNotification } from '../../notifications/useNotification';
import TourButton from '../../TourButton/TourButton';
import { getEditDietModalSteps } from '../../../tours/editDietModal';
import MenuItem from '@mui/material/MenuItem';

const EditDietModal = ({ setShowModal, diet, onSave, isNew = false }) => {
    const { t, i18n } = useTranslation();
    const tourSteps = useMemo(() => getEditDietModalSteps(t), [t]);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [loading, setLoading] = useState(false);
    const [editedDiet, setEditedDiet] = useState(structuredClone(diet)); // State to track the edited diet
    const { appColors, dietist } = useAppContext();
    const { showSuccess, showError } = useNotification();

    const adapterLocale = i18n.language.startsWith('it') ? 'it' : 'en-gb';

    const [startDate, setStartDate] = useState(diet.startDate ? dayjs(diet.startDate) : null);
    const [endDate,   setEndDate]   = useState(diet.endDate   ? dayjs(diet.endDate)   : null);
    const [language,  setLanguage]  = useState(diet.language  ?? '');
    const [errors,    setErrors]    = useState({});

    const validateForm = () => {
        const newErrors = {};
        if (!startDate) newErrors.startDate = t('errorMessages.dateRequired');
        if (!endDate)   newErrors.endDate   = t('errorMessages.dateRequired');
        if (startDate && endDate && startDate.isAfter(endDate))
            newErrors.startDate = t('errorMessages.startDateCantBeGreaterThanEndDate');
        if (isNew && !language)
            newErrors.language = t('errorMessages.diet-language-required');

        // Require at least one meal containing at least one food
        const dietPlanObj = editedDiet.dietPlan ?? editedDiet;
        const hasFood = Object.values(dietPlanObj).some(
            day => typeof day === 'object' && Object.values(day).some(
                meal => Array.isArray(meal?.foods) && meal.foods.length > 0
            )
        );
        if (!hasFood)
            newErrors.dietPlan = t('errorMessages.empty-diet-plan');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Save-as-template state
    const [templateRowOpen, setTemplateRowOpen] = useState(false);
    const [templateTitle,   setTemplateTitle]   = useState('');
    const templateRowRef = useRef(null);
    const modalContentRef = useRef(null);

    useEffect(() => {
        if (templateRowOpen && templateRowRef.current) {
            templateRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [templateRowOpen]);

    const dietTheme = createTheme({
        palette: {
            primary: {
                main:  '#fd4701',
                light: '#fc9f5b',
                dark:  '#830102',
            },
        },
    });

    const handleSaveAsTemplate = async () => {
        if (!templateTitle.trim()) return;
        try {
            setLoading(true);
            const payload = {
                title:        templateTitle.trim(),
                language:     i18n.language,
                dietPlan:     editedDiet.dietPlan ?? editedDiet,
                dietist:      dietist._id,
                intolerances: editedDiet.intolerances ?? [],
                illnesses:    editedDiet.illnesses    ?? [],
                isAlternatives: Boolean(editedDiet.isAlternatives),
                dietType: editedDiet.dietType ?? 'omnivore',
            };
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/diet-templates`,
                payload,
                { withCredentials: true }
            );
            showSuccess(t('template-saved'));
            setTemplateRowOpen(false);
            setTemplateTitle('');
        } catch (error) {
            console.error('Save template error:', error);
            showError(t('template-not-saved'));
        } finally {
            setLoading(false);
        }
    };

    const handleDietChange = (newDiet) => {
        setEditedDiet(newDiet);
    }

    const handleClose = () => {
        setShowModal(false);
    }

    const handleSubmit = async () => {
        if (!validateForm()) {
            modalContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        try {
            setLoading(true);
            if (isNew) {
                const payload = {
                    ...editedDiet,
                    startDate: startDate.toISOString(),
                    endDate:   endDate.toISOString(),
                    language:  language,
                    dietist:   dietist._id,
                };
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/diets`, payload, { withCredentials: true });
            } else {
                const payload = {
                    ...editedDiet,
                    startDate: startDate ? startDate.toISOString() : editedDiet.startDate,
                    endDate:   endDate   ? endDate.toISOString()   : editedDiet.endDate,
                };
                await axios.put(`${process.env.REACT_APP_BACKEND_URL}/diets/${diet._id}`, payload, { withCredentials: true });
            }

            if (onSave) {
                onSave();
            }

        } catch (error) {
            console.error('Edit diet modal error:', error);
            showError(t('unknown-error'));
        } finally {
            setLoading(false);
            handleClose();
        }
    };

    return (
        <>
        <LoadingOverlay open={loading} />
        <div className="modal-overlay">
            <div id='edit-diet-modal' className="modal-content" ref={modalContentRef} data-tour="edm-modal">
                <div id='modal-title'>
                    <h2>{isNew ? t("new-diet") : t("edit-diet")}</h2>
                    <div className="edm-tutorial-btn">
                        <TourButton steps={tourSteps} iconOnly={isMobile} label={t('tour.tutorial-btn')} />
                    </div>
                    <div id='close-modal-icon-wrapper' onClick={() => handleClose()}>
                        <CloseIcon />
                    </div>
                </div>
                <ThemeProvider theme={dietTheme}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                <div id='edit-diet-dates-row' data-tour="edm-dates">
                    <DatePicker
                        label={t('start-date')}
                        value={startDate}
                        onChange={(val) => { setStartDate(val); setErrors(p => ({ ...p, startDate: undefined })); }}
                        sx={muiDatePickerSx}
                        slotProps={{
                            textField: {
                                size: 'small',
                                error: Boolean(errors.startDate),
                                helperText: errors.startDate,
                            }
                        }}
                    />
                    <DatePicker
                        label={t('end-date')}
                        value={endDate}
                        onChange={(val) => { setEndDate(val); setErrors(p => ({ ...p, endDate: undefined })); }}
                        sx={muiDatePickerSx}
                        slotProps={{
                            textField: {
                                size: 'small',
                                error: Boolean(errors.endDate),
                                helperText: errors.endDate,
                            }
                        }}
                    />
                </div>
                {isNew && (
                    <div id='edit-diet-language-row' data-tour="edm-language">
                        <FormControl size='small' error={Boolean(errors.language)} sx={{ minWidth: 140, ...muiSelectSx, height: 'auto' }}>
                            <InputLabel id='new-diet-lang-label'>{t('Language')}</InputLabel>
                            <MuiSelect
                                labelId='new-diet-lang-label'
                                value={language}
                                label={t('Language')}
                                onChange={(e) => { setLanguage(e.target.value); setErrors(p => ({ ...p, language: undefined })); }}
                                sx={muiSelectSx}
                            >
                                <MenuItem value='it' sx={muiMenuItemSx}>IT</MenuItem>
                                <MenuItem value='en' sx={muiMenuItemSx}>EN</MenuItem>
                            </MuiSelect>
                            {errors.language && <FormHelperText>{errors.language}</FormHelperText>}
                        </FormControl>
                    </div>
                )}
                </LocalizationProvider>
                </ThemeProvider>
                <div id='modal-body' data-tour="edm-table">
                    {diet?.isAlternatives ? (
                        <AlternativesDietTable
                            diet={diet}
                            editedDiet={editedDiet}
                            editing={true}
                            onDietChange={handleDietChange}
                        />
                    ) : (
                        <DietTable
                            diet={diet}
                            editedDiet={editedDiet}
                            editing={true}
                            onDietChange={handleDietChange}
                        />
                    )}
                </div>
                <div className="modal-actions" data-tour="edm-footer">
                    <Button label={t("Cancel")} onClick={() => handleClose()} />
                    <Button
                        label={t("save-as-template")}
                        onClick={() => setTemplateRowOpen(prev => !prev)}
                        style={{ width: 'auto', fontSize: '13px', padding: '6px 16px', height: '43px' }}
                    />
                    <Button label={t("Save")} onClick={() => handleSubmit()} />
                </div>
                {errors.dietPlan && (
                    <p id='edit-diet-plan-error'>{errors.dietPlan}</p>
                )}
                {templateRowOpen && (
                    <ThemeProvider theme={dietTheme}>
                    <div id='edit-diet-template-row' ref={templateRowRef}>
                        <TextField
                            size='small'
                            label={t('template-title')}
                            value={templateTitle}
                            onChange={(e) => setTemplateTitle(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveAsTemplate()}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'var(--background-color)',
                                    '& fieldset': { borderColor: '#ccc' },
                                    '&:hover fieldset': { borderColor: 'var(--secondary-color)' },
                                    '&.Mui-focused fieldset': { borderColor: 'var(--primary-color)' },
                                },
                                '& .MuiInputLabel-root': { color: '#555' },
                                '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
                            }}
                        />
                        <Button
                            label={t('confirm-template')}
                            onClick={handleSaveAsTemplate}
                            style={{ fontSize: '13px', padding: '6px 16px', width: 'auto', height: '40px' }}
                        />
                    </div>
                    </ThemeProvider>
                )}
            </div>
        </div>
        </>
    );
};

export default EditDietModal;
